import React, { useState } from 'react';
import {
  ChevronDown,
  Info,
  Building2,
  Mail,
  Palette,
  HelpCircle,
  CreditCard,
  Globe,
  Wallet,
  LayoutTemplate,
  ListOrdered,
  Bell,
  Landmark,
} from 'lucide-react';
import EmailConfig from '../EmailConfigScreen/EmailConfig';
import AdminInfo from '../AdminInfo/AdminInfo';
import ColorConfig from '../ColorConfigScreen/ColorConfig';
import FaqQuestionsConfig from '../../components/FaqQuestionsConfig/FaqQuestionsConfig';
import MercadoPagoConfig from '../../components/MercadoPagoConfig/MercadoPagoConfig';
import PublicPagesConfig from '../../components/PublicPagesConfig/PublicPagesConfig';
import NotificationsConfig from '../../components/NotificationsConfig/NotificationsConfig';
import PaymentMethodConfig from '../../components/PaymentMethodConfig/PaymentMethodConfig';
import CuentasBancariasConfig from '../../components/CuentasBancariasConfig/CuentasBancariasConfig';
import HeroConfig from '../../components/HeroConfig/HeroConfig';
import HowItWorksConfig from '../../components/HowItWorksConfig/HowItWorksConfig';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/shared/PageHeader';
import { cn } from '../../lib/utils';

/**
 * Accordion item with icon, title and subtitle — matches the gotabooking config pattern.
 */
function AccordionItem({ icon: Icon, label, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className='overflow-hidden rounded-xl border border-border bg-card shadow-sm'>
      <button
        type='button'
        onClick={() => setOpen(prev => !prev)}
        className='flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        {Icon && (
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            <Icon className='h-5 w-5' />
          </div>
        )}
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-semibold text-foreground'>{label}</p>
          {subtitle && <p className='mt-0.5 text-xs text-muted-foreground'>{subtitle}</p>}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && <div className='border-t border-border px-5 py-5'>{children}</div>}
    </div>
  );
}

export default function AdminConfigScreen() {
  const { t } = useTranslation();

  return (
    <div className='space-y-6'>
      {/* Info banner */}
      <div className='flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground'>
        <Info className='mt-0.5 h-4 w-4 shrink-0 text-primary' />
        <span>
          Datos de la empresa, emails, colores, preguntas frecuentes, métodos de pago y
          configuración de la página de inicio. Los cambios afectan la apariencia y opciones de la
          plataforma.
        </span>
      </div>

      <PageHeader
        title={t('admin.setup')}
        subtitle='Administra la configuración de la plataforma'
      />

      <div className='space-y-3'>
        <AccordionItem
          icon={Building2}
          label={t('admin.companyInfo')}
          subtitle='Nombre, logo, dirección y contacto'
          defaultOpen
        >
          <AdminInfo />
        </AccordionItem>

        <AccordionItem
          icon={Mail}
          label={t('admin.systemEmails')}
          subtitle='Configuración de correos automáticos del sistema'
        >
          <EmailConfig />
        </AccordionItem>

        <AccordionItem
          icon={Bell}
          label='Notificaciones'
          subtitle='Asunto y contenido de los correos que se envían a usuarios y compradores'
        >
          <NotificationsConfig />
        </AccordionItem>

        <AccordionItem
          icon={Palette}
          label={t('admin.systemColors')}
          subtitle='Colores de la tienda pública'
        >
          <ColorConfig />
        </AccordionItem>

        <AccordionItem
          icon={HelpCircle}
          label={t('nav.faq')}
          subtitle='Preguntas frecuentes visibles en la tienda'
        >
          <FaqQuestionsConfig />
        </AccordionItem>

        <AccordionItem
          icon={CreditCard}
          label='Mercado Pago'
          subtitle='Access token y webhooks para pagos con Checkout Pro'
        >
          <MercadoPagoConfig />
        </AccordionItem>

        <AccordionItem
          icon={Globe}
          label={t('admin.publicPages')}
          subtitle='Textos e imágenes de las páginas públicas'
        >
          <PublicPagesConfig />
        </AccordionItem>

        <AccordionItem
          icon={Wallet}
          label='Métodos de pago'
          subtitle='Configurá los métodos de pago visibles en la tienda y en la página de inicio'
        >
          <PaymentMethodConfig />
        </AccordionItem>

        <AccordionItem
          icon={Landmark}
          label='Cuentas de destino'
          subtitle='Cuentas que reciben el dinero de cada venta y que el comprador puede elegir'
        >
          <CuentasBancariasConfig />
        </AccordionItem>

        <AccordionItem
          icon={LayoutTemplate}
          label='Hero / Carrusel de inicio'
          subtitle='Slides del banner principal — imagen, título, subtítulo y botón'
        >
          <HeroConfig />
        </AccordionItem>

        <AccordionItem
          icon={ListOrdered}
          label='¿Cómo funciona? — Pasos'
          subtitle='Textos de los 3 pasos del proceso de compra'
        >
          <HowItWorksConfig />
        </AccordionItem>
      </div>
    </div>
  );
}
